import { useState, useEffect, useRef, type ReactNode } from 'react';
import { collection, doc, serverTimestamp, writeBatch } from 'firebase/firestore';
import { logActivity } from '../utils/activity';
import { getClientDb } from '../firebase/clientApp';
import { useAuth } from '../context/AuthContext';
import { Button, Modal, ModalBanner, ConfirmModal, FormField, Input } from './ui';
import { EntityCombobox } from './ui/EntityCombobox';
import { CURRENCY_LIST, getCurrencySymbol } from '../utils/currency';
import { COUNTRIES } from '../utils/countries';
import { cleanAddress, formatAddressOneLine, legacyToBillTo } from '../utils/address';
import { useSettings } from '../hooks/useSettings';
import { isIndian, INDIAN_STATES } from '../utils/tradingSystem';
import type { Entity, EntityType, KYCStatus, EntityAddress } from '../types';

// ── Constants ─────────────────────────────────────────────────────────────────

export const TRADE_TYPES:   EntityType[] = ['customer', 'supplier', 'broker'];
export const SERVICE_TYPES: EntityType[] = ['shipping', 'insurance', 'lab'];

const ENTITY_TYPE_DEFS = [
  { value: 'customer'  as EntityType, label: 'Customer',  group: 'trade'   },
  { value: 'supplier'  as EntityType, label: 'Supplier',  group: 'trade'   },
  { value: 'broker'    as EntityType, label: 'Broker',    group: 'trade'   },
  { value: 'shipping'  as EntityType, label: 'Shipping',  group: 'service' },
  { value: 'insurance' as EntityType, label: 'Insurance', group: 'service' },
  { value: 'lab'       as EntityType, label: 'Lab',       group: 'service' },
];

const TRADE_DEFS   = ENTITY_TYPE_DEFS.filter(d => d.group === 'trade');
const SERVICE_DEFS = ENTITY_TYPE_DEFS.filter(d => d.group === 'service');

const KYC_DOC_TYPES = [
  'Passport', 'National ID', 'Business Registration',
  'VAT Certificate', 'Trade License', 'Other',
];

const KYC_STATUS_OPTIONS = [
  { value: 'not-submitted', label: 'Not Submitted' },
  { value: 'pending',       label: 'Pending Review' },
  { value: 'verified',      label: 'Verified ✓'    },
];

const SHIPPING_MODES = ['Air', 'Road', 'Sea', 'Rail'];
const COVERAGE_TYPES = ['Cargo', 'Transit', 'All-risk', 'Storage'];

const BLANK_ADDR: EntityAddress = { name: '', line1: '', line2: '', city: '', state: '', postalCode: '', country: '' };

// EntityCombobox is built for {id, companyName} entities, but a type-to-filter
// dropdown is exactly what a long, alphabetical country <select> needs too —
// mapping each country to id===companyName===the country itself reuses it
// with zero new component code, so every Country field in this file (and any
// future one) types-and-selects instead of scrolling a native <select>.
const COUNTRY_OPTIONS = COUNTRIES.map(c => ({ id: c, companyName: c }));

const EMPTY_FORM = {
  types:         [] as EntityType[],
  companyName:   '',
  email:         '',
  accountantEmail: '',
  vatGstBtw:     '',
  country:       '',
  defaultCurrency: '',
  website:       '',
  defaultTerms:  '',
  creditLimitUSD: '',
  defaultLessPercent: '',
  billTo:        { ...BLANK_ADDR } as EntityAddress,
  shipTo:        { ...BLANK_ADDR } as EntityAddress,
  shipToSameAsBillTo: true,
  phone:         '',
  ownerName:     '',
  ownerPhone:    '',
  ownerEmail:    '',
  kycStatus:      'not-submitted' as KYCStatus,
  kycDocType:     '',
  kycDocNumber:   '',
  kycExpiryDate:  '',
  kycNotes:       '',
  accountNumber: '',
  shippingModes: [] as string[],
  coverageTypes: [] as string[],
  agentName:     '',
  notes:         '',
};

type EntityForm = typeof EMPTY_FORM;

// ── Helpers ───────────────────────────────────────────────────────────────────

export function needsKYC(types: EntityType[]) {
  return types.includes('customer') || types.includes('supplier');
}
export function isService(types: EntityType[]) {
  return types.some(t => SERVICE_TYPES.includes(t));
}

// ── Icons ─────────────────────────────────────────────────────────────────────

function IconCheck() {
  return (
    <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
      <path d="M2.5 7.2l3 3 6-6.4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
// Section icons (14px line icons, coloured by the section's icon tile).
const SECTION_ICONS = {
  company: <path d="M2.5 13.5V3.5a1 1 0 011-1h5a1 1 0 011 1v10M9.5 6.5h3a1 1 0 011 1v6M1.5 13.5h13M5 5h2M5 7.5h2M5 10h2M11.5 9.5h0M11.5 11.5h0" />,
  owner:   <><circle cx="8" cy="5" r="2.6" /><path d="M2.8 13.5c.6-2.6 2.7-4 5.2-4s4.6 1.4 5.2 4" /></>,
  terms:   <><path d="M2.5 4.5h7M12 4.5h1.5M2.5 8h2M7 8h6.5M2.5 11.5h6M11 11.5h2.5" /><circle cx="10.5" cy="4.5" r="1.4" /><circle cx="5.5" cy="8" r="1.4" /><circle cx="9.5" cy="11.5" r="1.4" /></>,
  address: <><path d="M8 14s4.5-4.2 4.5-7.6A4.5 4.5 0 003.5 6.4C3.5 9.8 8 14 8 14z" /><circle cx="8" cy="6.4" r="1.6" /></>,
  service: <><path d="M1.5 4.5h8v6h-8zM9.5 7h2.8l2.2 2.2v1.3h-5" /><circle cx="4" cy="11.6" r="1.2" /><circle cx="11.5" cy="11.6" r="1.2" /></>,
  kyc:     <><path d="M8 1.8l5 1.8v3.8c0 3.3-2.1 5.8-5 6.6-2.9-.8-5-3.3-5-6.6V3.6l5-1.8z" /><path d="M5.6 7.8l1.6 1.6 3.2-3.2" /></>,
  notes:   <><path d="M3.5 1.8h6.2l2.8 2.8v9.6h-9z" /><path d="M5.5 7.5h5M5.5 10h5M5.5 5h2.5" /></>,
};

/** One titled card in the form: icon tile + small uppercase title, then its fields. */
function FormSection({ icon, title, aside, tone, children }: {
  icon: keyof typeof SECTION_ICONS; title: string; aside?: ReactNode;
  /** Icon colour (a CSS colour); primary by default. */
  tone?: string; children: ReactNode;
}) {
  return (
    <section className="ef-card" style={tone ? { ['--ef-tone' as string]: tone } : undefined}>
      <div className="ef-card-head">
        <span className="ef-card-icon">
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
            {SECTION_ICONS[icon]}
          </svg>
        </span>
        <span className="ef-card-title">{title}</span>
        {aside && <span className="ef-card-aside">{aside}</span>}
      </div>
      {children}
    </section>
  );
}

// ── Compact type pill ─────────────────────────────────────────────────────────

function TypePill({
  value, label, selected, onToggle,
}: {
  value: EntityType; label: string; selected: boolean; onToggle: () => void;
}) {
  return (
    <button
      type="button"
      className={`type-pill type-pill--${value}${selected ? ' type-pill--on' : ''}`}
      onClick={onToggle}
    >
      <span className="type-pill-dot" />
      {label}
    </button>
  );
}

// ── Check chip (for multi-select option lists) ────────────────────────────────

function CheckChip({ label, checked, onToggle }: { label: string; checked: boolean; onToggle: () => void }) {
  return (
    <label className={`check-chip${checked ? ' check-chip--on' : ''}`}>
      <input type="checkbox" hidden checked={checked} onChange={onToggle} />
      {checked && (
        <svg width="9" height="9" viewBox="0 0 9 9" fill="none" style={{ flexShrink: 0 }}>
          <path d="M1.5 4.5l2.5 2.5 4-4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )}
      {label}
    </label>
  );
}

// ── Structured address fields (Bill To / Ship To) ────────────────────────────

// All on one line, in the order an address is written.
function AddressFields({
  value, onChange, indianStates,
}: {
  value: EntityAddress;
  onChange: (field: keyof EntityAddress, val: string) => void;
  /** Indian trading system: State is picked from the list (it decides CGST + SGST or IGST). */
  indianStates?: boolean;
}) {
  const v = (k: keyof EntityAddress) => value[k] ?? '';
  return (
    <div className="ef-line ef-line-address">
        <FormField label="Attention / Name">
          <input value={v('name')} onChange={e => onChange('name', e.target.value)} placeholder="Name" />
        </FormField>
        <FormField label="Address Line 1">
          <input value={v('line1')} onChange={e => onChange('line1', e.target.value)} placeholder="Street and number" />
        </FormField>
        <FormField label="Address Line 2">
          <input value={v('line2')} onChange={e => onChange('line2', e.target.value)} placeholder="Suite, unit, floor…" />
        </FormField>
        <FormField label="City">
          <input value={v('city')} onChange={e => onChange('city', e.target.value)} placeholder="Antwerp" />
        </FormField>
        <FormField label="State / Province">
          {indianStates && (!v('country') || v('country') === 'India') ? (
            <select value={v('state')} onChange={e => onChange('state', e.target.value)}>
              <option value="">Select…</option>
              {v('state') && !INDIAN_STATES.includes(v('state')) && <option value={v('state')}>{v('state')}</option>}
              {INDIAN_STATES.map(st => <option key={st} value={st}>{st}</option>)}
            </select>
          ) : (
            <input value={v('state')} onChange={e => onChange('state', e.target.value)} placeholder="Flanders" />
          )}
        </FormField>
        <FormField label="Postal Code">
          <input value={v('postalCode')} onChange={e => onChange('postalCode', e.target.value)} placeholder="2018" />
        </FormField>
        <FormField label="Country">
          <EntityCombobox
            entities={COUNTRY_OPTIONS}
            value={v('country')}
            onChange={val => onChange('country', val)}
            placeholder="Type to search country…"
          />
        </FormField>
    </div>
  );
}

// ── EntityFormModal ─────────────────────────────────────────────────────────────
//
// Shared Add/Edit Entity form, used both by the Entity page itself and by any
// other page (Purchase, Sales, …) that needs a "+ Create New" quick-add flow
// without navigating away. Pass `entity` to edit an existing one, or
// `defaultTypes` to preselect a type (e.g. ['supplier']) when creating fresh.

export function EntityFormModal({
  open, onClose, entity, defaultTypes, onSaved,
}: {
  open:          boolean;
  onClose:       () => void;
  entity?:       Entity | null;
  defaultTypes?: EntityType[];
  onSaved?:      (entity: Entity) => void;
}) {
  const { user, business } = useAuth();
  const settings = useSettings();
  const currencySymbol = getCurrencySymbol(settings.currency);
  const isAdd = !entity;

  const [form,      setForm]      = useState<EntityForm>(EMPTY_FORM);
  const [saving,    setSaving]    = useState(false);
  const [formError, setFormError] = useState('');
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  // The form as it was when the modal opened — closing with anything
  // different asks first (same Discard / keep editing prompt as the
  // Purchase/Sale invoice modals).
  const baselineRef = useRef('');

  useEffect(() => {
    if (!open) return;
    let initial: EntityForm;
    if (entity) {
      initial = {
        types:         entity.types,
        companyName:   entity.companyName,
        email:         entity.email          ?? '',
        accountantEmail: entity.accountantEmail ?? '',
        vatGstBtw:     entity.vatGstBtw      ?? '',
        country:       entity.country         ?? '',
        defaultCurrency: entity.defaultCurrency ?? '',
        website:       entity.website         ?? '',
        defaultTerms:  entity.defaultTerms != null ? String(entity.defaultTerms) : '',
        creditLimitUSD: entity.creditLimitUSD != null ? String(entity.creditLimitUSD) : '',
        defaultLessPercent: entity.defaultLessPercent != null ? String(entity.defaultLessPercent) : '',
        billTo:        { ...BLANK_ADDR, ...(entity.billTo ?? legacyToBillTo(entity.address)) },
        shipTo:        { ...BLANK_ADDR, ...(entity.shipTo ?? {}) },
        shipToSameAsBillTo: entity.shipToSameAsBillTo ?? false,
        phone:         entity.phone,
        ownerName:     entity.ownerName,
        ownerPhone:    entity.ownerPhone,
        ownerEmail:    entity.ownerEmail    ?? '',
        kycStatus:      entity.kycStatus,
        kycDocType:     entity.kycDocType      ?? '',
        kycDocNumber:   entity.kycDocNumber    ?? '',
        kycExpiryDate:  entity.kycExpiryDate   ?? '',
        kycNotes:       entity.kycNotes        ?? '',
        accountNumber: (entity as any).accountNumber  ?? '',
        shippingModes: (entity as any).shippingModes  ?? [],
        coverageTypes: (entity as any).coverageTypes  ?? [],
        agentName:     (entity as any).agentName      ?? '',
        notes:         entity.notes          ?? '',
      };
    } else {
      // "+ Create New" from Purchase / Sales: a customer or supplier starts as both.
      const dt = defaultTypes ?? [];
      initial = { ...EMPTY_FORM, types: dt.length === 1 && (dt[0] === 'customer' || dt[0] === 'supplier') ? ['customer', 'supplier'] : dt };
    }
    setForm(initial);
    baselineRef.current = JSON.stringify(initial);
    setFormError('');
    setConfirmDiscard(false);
  }, [open, entity]); // eslint-disable-line react-hooks/exhaustive-deps

  function set<K extends keyof EntityForm>(key: K, val: EntityForm[K]) {
    setForm(f => ({ ...f, [key]: val }));
  }
  function setAddr(which: 'billTo' | 'shipTo', field: keyof EntityAddress, val: string) {
    setForm(f => ({ ...f, [which]: { ...f[which], [field]: val } }));
  }

  function toggleType(type: EntityType) {
    setForm(f => {
      // Customer and Supplier go together: clicking either one ticks (or
      // unticks) both.
      if (type === 'customer' || type === 'supplier') {
        const pair: EntityType[] = ['customer', 'supplier'];
        const on = f.types.includes(type);
        const rest = f.types.filter(t => !pair.includes(t) && TRADE_TYPES.includes(t));
        return { ...f, types: on ? rest : [...rest, ...pair] };
      }
      if (f.types.includes(type)) {
        return { ...f, types: f.types.filter(t => t !== type) };
      }
      if (SERVICE_TYPES.includes(type)) {
        // Service types are mutually exclusive — select only this one
        return { ...f, types: [type] };
      }
      // Trade types: multi-select allowed, but strip any service types
      const kept = f.types.filter(t => TRADE_TYPES.includes(t));
      return { ...f, types: [...kept, type] };
    });
  }

  function toggleMode(mode: string) {
    setForm(f => {
      const has = f.shippingModes.includes(mode);
      return { ...f, shippingModes: has ? f.shippingModes.filter(m => m !== mode) : [...f.shippingModes, mode] };
    });
  }

  function toggleCoverage(ct: string) {
    setForm(f => {
      const has = f.coverageTypes.includes(ct);
      return { ...f, coverageTypes: has ? f.coverageTypes.filter(c => c !== ct) : [...f.coverageTypes, ct] };
    });
  }

  function validate(): string {
    if (form.types.length === 0)   return 'Select at least one entity type.';
    if (!form.companyName.trim())  return 'Company name is required.';
    return '';
  }

  async function handleSave() {
    const err = validate();
    if (err) { setFormError(err); return; }
    setSaving(true);
    setFormError('');
    const db = getClientDb();
    if (!db) { setFormError('Database not connected. Please refresh and try again.'); setSaving(false); return; }

    const svcEntity = isService(form.types);
    const billTo = cleanAddress(form.billTo);
    const shipTo = form.shipToSameAsBillTo ? null : cleanAddress(form.shipTo);
    const basePayload: Record<string, unknown> = {
      businessId:   business!.id,
      types:        form.types,
      companyName:  form.companyName.trim(),
      // Deprecated flat field — kept in sync from billTo so anything still
      // reading `.address` keeps working.
      address:      formatAddressOneLine(billTo),
      billTo,
      shipTo,
      shipToSameAsBillTo: form.shipToSameAsBillTo,
      country:      form.country.trim()      || null,
      defaultCurrency: form.defaultCurrency  || null,
      website:      form.website.trim()      || null,
      defaultTerms: (form.types.includes('customer') || form.types.includes('supplier')) && form.defaultTerms.trim() ? (parseInt(form.defaultTerms, 10) || null) : null,
      creditLimitUSD: form.types.includes('customer') && form.creditLimitUSD.trim() ? (parseFloat(form.creditLimitUSD) || null) : null,
      defaultLessPercent: form.types.includes('customer') && parseFloat(form.defaultLessPercent) > 0 && parseFloat(form.defaultLessPercent) < 100
        ? parseFloat(form.defaultLessPercent) : null,
      email:        form.email.trim().toLowerCase(),
      accountantEmail: form.accountantEmail.trim().toLowerCase() || null,
      vatGstBtw:    form.vatGstBtw.trim()    || null,
      phone:        form.phone.trim(),
      ownerName:    form.ownerName.trim(),
      ownerPhone:   form.ownerPhone.trim(),
      ownerEmail:   form.ownerEmail.trim().toLowerCase() || null,
      kycStatus:     needsKYC(form.types) ? form.kycStatus : 'not-submitted',
      kycDocType:    form.kycDocType.trim()    || null,
      kycDocNumber:  form.kycDocNumber.trim()  || null,
      kycExpiryDate: form.kycExpiryDate.trim() || null,
      kycNotes:      form.kycNotes.trim()      || null,
      notes:        form.notes.trim()        || null,
      isActive:     true,
    };

    if (svcEntity) {
      basePayload.accountNumber = form.accountNumber.trim() || null;
      if (form.types.includes('shipping'))  basePayload.shippingModes = form.shippingModes;
      if (form.types.includes('insurance')) {
        basePayload.coverageTypes = form.coverageTypes;
        basePayload.agentName     = form.agentName.trim() || null;
      }
    }

    try {
      // The company and its Activity Log line are saved together.
      const batch = writeBatch(db);
      if (isAdd) {
        const docRef = doc(collection(db, 'entities'));
        batch.set(docRef, {
          ...basePayload, createdAt: serverTimestamp(), updatedAt: serverTimestamp(), createdBy: user!.id,
        });
        logActivity(batch, db, business!.id, user, {
          module: 'entity', action: 'create', recordId: docRef.id, recordNo: form.companyName.trim(),
          note: form.types.join(', '),
        });
        await batch.commit();
        const now = new Date().toISOString();
        onSaved?.({ id: docRef.id, ...basePayload, createdAt: now, updatedAt: now, createdBy: user!.id } as Entity);
      } else if (entity) {
        batch.update(doc(db, 'entities', entity.id), { ...basePayload, updatedAt: serverTimestamp() });
        logActivity(batch, db, business!.id, user, {
          module: 'entity', action: 'edit', recordId: entity.id, recordNo: form.companyName.trim(),
        });
        await batch.commit();
        onSaved?.({ ...entity, ...basePayload, updatedAt: new Date().toISOString() } as Entity);
      }
    } catch (e: unknown) {
      setFormError(e instanceof Error ? e.message : 'Failed to save. Check your connection.');
      setSaving(false);
      return;
    }
    setSaving(false);
  }

  const showKYC     = needsKYC(form.types);
  const showService = isService(form.types);
  const isCustomer  = form.types.includes('customer');
  const isSupplier  = form.types.includes('supplier');

  // Escape, the backdrop, the × button, and Cancel all funnel through this.
  function requestClose() {
    if (JSON.stringify(form) !== baselineRef.current) setConfirmDiscard(true);
    else onClose();
  }

  return (
    <>
    <Modal
      open={open}
      onClose={requestClose}
      hideHead
      size="full"
      footer={
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, width: '100%' }}>
          {formError && <div className="msg msg-error" style={{ margin: 0 }}>{formError}</div>}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 11.5, color: 'var(--text-3)', marginRight: 'auto' }}>
              <span style={{ color: 'var(--danger)' }}>*</span> Required fields
            </span>
            <Button variant="secondary" onClick={requestClose} disabled={saving}>Cancel</Button>
            <Button variant="primary" icon={<IconCheck />} onClick={handleSave} loading={saving}>
              {isAdd ? 'Add Entity' : 'Save Changes'}
            </Button>
          </div>
        </div>
      }
    >
      <ModalBanner
        title={form.companyName.trim() || (isAdd ? 'New Entity' : 'Untitled Entity')}
        eyebrow={isAdd ? 'Add Entity' : 'Edit Entity'}
        onClose={requestClose}
      />

      <div className="entity-modal-subhead" style={{ marginTop: 0 }}>Entity Type</div>
      <div className="type-pill-row">
        {TRADE_DEFS.map(def => (
          <TypePill
            key={def.value}
            value={def.value}
            label={def.label}
            selected={form.types.includes(def.value)}
            onToggle={() => toggleType(def.value)}
          />
        ))}
        <span className="type-pill-sep" />
        {SERVICE_DEFS.map(def => (
          <TypePill
            key={def.value}
            value={def.value}
            label={def.label}
            selected={form.types.includes(def.value)}
            onToggle={() => toggleType(def.value)}
          />
        ))}
      </div>
      <p className="form-hint" style={{ marginTop: 10, marginBottom: 0 }}>
        {showService
          ? 'Service providers are separate from trade partners.'
          : 'A trade entity can be Customer and Supplier simultaneously.'}
      </p>

      <div className="entity-modal-divider" style={{ margin: '18px 0 16px' }} />

      <FormSection icon="company" title={showService ? 'Company' : 'Company Information'}>
        <div className={`ef-line${showService ? '' : ' ef-line-company'}`}>
          <FormField label="Company Name" required>
            <input value={form.companyName} onChange={e => set('companyName', e.target.value)} placeholder="ABC Diamonds Ltd." />
          </FormField>
          {!showService && (
            <FormField label="VAT / GST / BTW No.">
              <input value={form.vatGstBtw} onChange={e => set('vatGstBtw', e.target.value)} placeholder="BE0123456789" />
            </FormField>
          )}
          {showService && (
            <FormField label="Country">
              <EntityCombobox entities={COUNTRY_OPTIONS} value={form.country} onChange={val => set('country', val)} placeholder="Type to search country…" />
            </FormField>
          )}
          {showService && (
            <FormField label="Website">
              <input value={form.website} onChange={e => set('website', e.target.value)} placeholder="www.company.com" />
            </FormField>
          )}
          <FormField label="Company Email">
            <input type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="contact@company.com" />
          </FormField>
          <FormField label="Phone">
            <input value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="+32 123 456 789" />
          </FormField>
          <FormField label="Accountant Email">
            <input type="email" value={form.accountantEmail} onChange={e => set('accountantEmail', e.target.value)} placeholder="accounts@company.com" />
          </FormField>
        </div>
      </FormSection>

      {/* Owner and Trading Terms side by side. Trading Terms: Country, Currency,
          Website, then Default Terms, Credit Limit, Keep(%) on a second line. */}
      <div className="ef-pair-wrap">
        <div className="ef-pair" style={{ ['--ef-pair' as string]: showService ? '1fr' : '1fr 1fr' }}>
        <FormSection icon="owner" title={showService ? 'Contact Person' : 'Owner'}>
          <div className="ef-pair-line" style={{ gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' }}>
            <FormField label={showService ? 'Contact Name' : 'Owner Name'}>
              <input value={form.ownerName} onChange={e => set('ownerName', e.target.value)} placeholder="John Smith" />
            </FormField>
            <FormField label={showService ? 'Contact Phone' : 'Owner Phone'}>
              <input value={form.ownerPhone} onChange={e => set('ownerPhone', e.target.value)} placeholder="+32 123 456 789" />
            </FormField>
            <FormField label={showService ? 'Contact Email' : 'Owner Email'}>
              <input type="email" value={form.ownerEmail} onChange={e => set('ownerEmail', e.target.value)} placeholder="owner@company.com" />
            </FormField>
          </div>
        </FormSection>

        {!showService && (
          <FormSection icon="terms" title="Trading Terms" tone="var(--success)">
            <div className="ef-pair-line" style={{ gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' }}>
              <FormField label="Country">
                <EntityCombobox entities={COUNTRY_OPTIONS} value={form.country} onChange={val => set('country', val)} placeholder="Type to search country…" />
              </FormField>
              <FormField label="Default Currency">
                <select value={form.defaultCurrency} onChange={e => set('defaultCurrency', e.target.value)} title="Fills in the invoice currency for this partner.">
                  <option value="">—</option>
                  {CURRENCY_LIST.map(c => <option key={c.code} value={c.code}>{c.code} · {c.name}</option>)}
                </select>
              </FormField>
              <FormField label="Website">
                <input value={form.website} onChange={e => set('website', e.target.value)} placeholder="www.company.com" />
              </FormField>
              {(isCustomer || isSupplier) && (
                <FormField label="Default Terms">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}
                    title={isCustomer && isSupplier ? 'Fills in Payment Terms on a new sale or purchase.' : isSupplier ? 'Fills in Payment Terms on a new purchase.' : 'Fills in Payment Terms on a new sale.'}>
                    <input type="number" min={1} className="no-spinner" value={form.defaultTerms}
                      onChange={e => set('defaultTerms', e.target.value)} placeholder="30" style={{ flex: 1 }} />
                    <span style={{ fontSize: 12, color: 'var(--text-3)', fontWeight: 500, flexShrink: 0 }}>days</span>
                  </div>
                </FormField>
              )}
              {isCustomer && (
                <FormField label="Credit Limit">
                  <div title="Sales warns once what this customer owes would go over it.">
                    <Input type="number" min={0} className="no-spinner" value={form.creditLimitUSD}
                      onChange={e => set('creditLimitUSD', e.target.value)} placeholder="e.g. 50000" prefix={currencySymbol} />
                  </div>
                </FormField>
              )}
              {isCustomer && (
                <FormField label="Default Keep(%)">
                  <div title="The share he keeps for his own client. Fills in on each new sale.">
                    <Input type="number" min={0} max={99} step="0.01" className="no-spinner" value={form.defaultLessPercent}
                      onChange={e => set('defaultLessPercent', e.target.value)} placeholder="0" suffix="%" />
                  </div>
                </FormField>
              )}
            </div>
          </FormSection>
        )}
        </div>
      </div>

      {showService ? (
        <FormSection icon="address" title="Address" tone="var(--warning)">
          <AddressFields value={form.billTo} onChange={(f, v) => setAddr('billTo', f, v)} indianStates={isIndian(settings)} />
        </FormSection>
      ) : (
        <FormSection icon="address" title="Bill To & Ship To" tone="var(--warning)">
          <div className="ef-subtitle">Bill To (invoice address)</div>
          <AddressFields value={form.billTo} onChange={(f, v) => setAddr('billTo', f, v)} indianStates={isIndian(settings)} />
          <label className="ef-check">
            <input
              type="checkbox"
              checked={form.shipToSameAsBillTo}
              onChange={e => set('shipToSameAsBillTo', e.target.checked)}
            />
            Ship To same as Bill To
          </label>
          {!form.shipToSameAsBillTo && (
            <div className="ef-shipto">
              <div className="ef-subtitle">Ship To (delivery address)</div>
              <AddressFields value={form.shipTo} onChange={(f, v) => setAddr('shipTo', f, v)} indianStates={isIndian(settings)} />
            </div>
          )}
        </FormSection>
      )}

      {showService && (
        <FormSection icon="service" title="Service Details">
          <div className="ef-line">
            <FormField label={form.types.includes('insurance') ? 'Policy / Account Number' : 'Account Number'}>
              <input
                value={form.accountNumber}
                onChange={e => set('accountNumber', e.target.value)}
                placeholder={form.types.includes('insurance') ? 'POL-2024-00123' : 'ACC-12345'}
              />
            </FormField>
            {form.types.includes('shipping') && (
              <FormField label="Shipping Modes">
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', paddingTop: 2 }}>
                  {SHIPPING_MODES.map(m => (
                    <CheckChip key={m} label={m} checked={form.shippingModes.includes(m)} onToggle={() => toggleMode(m)} />
                  ))}
                </div>
              </FormField>
            )}
            {form.types.includes('insurance') && (
              <>
                <FormField label="Agent Name">
                  <input value={form.agentName} onChange={e => set('agentName', e.target.value)} placeholder="Jane Doe" />
                </FormField>
                <FormField label="Coverage Types">
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', paddingTop: 2 }}>
                    {COVERAGE_TYPES.map(ct => (
                      <CheckChip key={ct} label={ct} checked={form.coverageTypes.includes(ct)} onToggle={() => toggleCoverage(ct)} />
                    ))}
                  </div>
                </FormField>
              </>
            )}
          </div>
        </FormSection>
      )}

      {showKYC && (
        <FormSection icon="kyc" title="KYC Verification" tone="var(--danger)">
          <div className="ef-line ef-line-kyc">
            <FormField label="KYC Status">
              <select value={form.kycStatus} onChange={e => set('kycStatus', e.target.value as KYCStatus)}>
                {KYC_STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </FormField>
            <FormField label="Document Type">
              <select value={form.kycDocType} onChange={e => set('kycDocType', e.target.value)}>
                <option value="">Select type…</option>
                {KYC_DOC_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </FormField>
            <FormField label="Document Number">
              <input value={form.kycDocNumber} onChange={e => set('kycDocNumber', e.target.value)} placeholder="e.g. PA1234567" />
            </FormField>
            <FormField label="Expiry Date">
              <input type="date" value={form.kycExpiryDate} onChange={e => set('kycExpiryDate', e.target.value)} />
            </FormField>
            <FormField label="KYC Notes">
              <input value={form.kycNotes} onChange={e => set('kycNotes', e.target.value)} placeholder="Who checked it, when…" />
            </FormField>
          </div>
        </FormSection>
      )}

      <FormSection icon="notes" title="Notes" tone="var(--text-3)">
        <FormField>
          <textarea value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Any additional details…" style={{ minHeight: 54 }} />
        </FormField>
      </FormSection>
    </Modal>

    <ConfirmModal
      open={confirmDiscard}
      title="Discard changes?"
      message="This entity has unsaved changes. Discard them and close, or keep editing?"
      confirmLabel="Discard"
      onConfirm={() => { setConfirmDiscard(false); onClose(); }}
      onClose={() => setConfirmDiscard(false)}
    />
    </>
  );
}
